import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReleaseMedia, ReleaseWindowRequest, RemmiRelease } from '@models/remmi-release.model';
import { RemmiReleasesService } from '@services/api/remmi-releases.service';
import { ToastrService } from 'ngx-toastr';
import { DialogModule } from 'primeng/dialog';
import { Subscription } from 'rxjs';
import { ReleasePillComponent } from '../release-pill/release-pill.component';

interface WindowSection {
  key: 'overview' | 'how' | 'example';
  label: string;
}

/**
 * "New feature in Remmi" window (design D3). Mounted once in the app shell; the banner, the
 * release history drawer and the admin Preview button open it through
 * `RemmiReleasesService.openWindow()`.
 *
 * Tracking rules (backend guide §2.2): every open records a view; only Finish on the last section
 * records "completed" — Close, ×, Esc and a backdrop click never do; a ticked "Don't show this
 * again" is sent when the release is left, however it is left. Preview mode records nothing.
 */
@Component({
  selector: 'app-feature-release-window',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ReleasePillComponent],
  templateUrl: './feature-release-window.component.html',
  styleUrls: ['./feature-release-window.component.scss'],
})
export class FeatureReleaseWindowComponent implements OnInit, OnDestroy {
  isVisible = false;
  mode: ReleaseWindowRequest['mode'] = 'single';
  /** List mode: releases still waiting for a decision. */
  listReleases: RemmiRelease[] = [];
  /** The release on screen; `null` in list mode means the list itself is showing. */
  release: RemmiRelease = null;
  isLoading = false;
  activeIndex = 0;
  exampleIndex = 0;
  isDontShowAgain = false;

  private subscription: Subscription;

  constructor(
    private releasesService: RemmiReleasesService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscription = this.releasesService.windowRequest$.subscribe(request => this.open(request));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get isTracked(): boolean {
    return this.mode !== 'preview';
  }

  /** Empty sections are left out, so the numbers stay 1, 2, 3 in order. */
  get sections(): WindowSection[] {
    if (!this.release) {
      return [];
    }
    const sections: WindowSection[] = [{ key: 'overview', label: 'Overview' }];
    if (this.release.how_it_works?.length) {
      sections.push({ key: 'how', label: 'How it works' });
    }
    if (this.images.length || this.videos.length) {
      sections.push({ key: 'example', label: 'Example' });
    }
    return sections;
  }

  get activeSection(): WindowSection {
    return this.sections[this.activeIndex];
  }

  get isLastSection(): boolean {
    return this.activeIndex >= this.sections.length - 1;
  }

  get images(): ReleaseMedia[] {
    return this.release?.images ?? [];
  }

  get videos(): ReleaseMedia[] {
    return this.release?.videos ?? [];
  }

  get isListView(): boolean {
    return this.mode === 'list' && !this.release && !this.isLoading;
  }

  open(request: ReleaseWindowRequest): void {
    this.mode = request.mode;
    this.listReleases = [];
    this.release = null;
    this.isDontShowAgain = false;
    this.isVisible = true;

    if (request.mode === 'single') {
      this.loadRelease(request.releaseId);
    } else if (request.mode === 'list') {
      this.listReleases = [...request.releases];
    } else {
      this.showRelease(request.release);
    }
  }

  viewFromList(item: RemmiRelease): void {
    this.listReleases = this.listReleases.filter(release => release._id !== item._id);
    this.loadRelease(item._id);
  }

  ignoreFromList(item: RemmiRelease): void {
    this.listReleases = this.listReleases.filter(release => release._id !== item._id);
    this.releasesService.ignore(item._id, 'feature_list').subscribe({ error: () => {} });
    if (!this.listReleases.length) {
      this.isVisible = false;
    }
  }

  backToList(): void {
    this.settleCurrent();
    this.release = null;
    if (!this.listReleases.length) {
      this.isVisible = false;
    }
  }

  goTo(index: number): void {
    this.activeIndex = index;
  }

  /** Steps back one section; never records anything (only Finish counts as completed). */
  back(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  next(): void {
    if (this.isLastSection) {
      this.finish();
      return;
    }
    this.activeIndex++;
  }

  close(): void {
    this.isVisible = false;
  }

  /** Every way out of the dialog (Close, ×, Esc, backdrop, Finish) ends here. */
  onHide(): void {
    this.settleCurrent();
    this.release = null;
    this.listReleases = [];
  }

  prevImage(): void {
    this.exampleIndex = (this.exampleIndex - 1 + this.images.length) % this.images.length;
  }

  nextImage(): void {
    this.exampleIndex = (this.exampleIndex + 1) % this.images.length;
  }

  trackById(_: number, item: { _id: string }): string {
    return item._id;
  }

  /** The only path that records "completed". In list mode it returns to the remaining features. */
  private finish(): void {
    if (this.isTracked && this.release) {
      this.releasesService.complete(this.release._id).subscribe({ error: () => {} });
    }
    if (this.mode === 'list' && this.listReleases.length) {
      this.backToList();
      return;
    }
    this.isVisible = false;
  }

  /** Sends a ticked "Don't show this again" for the release being left, then clears the box. */
  private settleCurrent(): void {
    if (this.isTracked && this.release && this.isDontShowAgain) {
      this.releasesService.dontShowAgain(this.release._id).subscribe({ error: () => {} });
    }
    this.isDontShowAgain = false;
  }

  /** Fetched fresh on every open: media URLs are signed and expire after 6 hours. */
  private loadRelease(id: string): void {
    this.isLoading = true;
    this.releasesService.getRelease(id).subscribe({
      next: release => {
        this.isLoading = false;
        this.showRelease(release);
        this.releasesService.view(id).subscribe({ error: () => {} });
      },
      error: err => {
        this.isLoading = false;
        this.toastr.error(this.releasesService.errorMessage(err, 'Could not load this feature.'));
        if (this.mode === 'list' && this.listReleases.length) {
          this.release = null;
          return;
        }
        this.isVisible = false;
      },
    });
  }

  private showRelease(release: RemmiRelease): void {
    this.release = release;
    this.activeIndex = 0;
    this.exampleIndex = 0;
    this.isDontShowAgain = false;
  }
}
